import { NgClass } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../../core/auth/auth.service';
import { WebrtcService } from '../webrtc.service';
import { CallSignal, CallTrace } from '../quick-chat.types';

export interface QuickChatCallSession {
    conversationId: number;
    peerId: number;
    contactName: string;
    mediaType: 'audio' | 'video';
}

@Component({
    selector: 'quick-chat-call',
    standalone: true,
    imports: [NgClass, MatButtonModule, MatIconModule],
    templateUrl: './quick-chat-call.component.html',
    styleUrls: ['./quick-chat-call.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [WebrtcService],
})
export class QuickChatCallComponent implements OnInit, OnChanges, OnDestroy {
    @Input() session: QuickChatCallSession | null = null;

    @Output() close = new EventEmitter<void>();
    @Output() callFinished = new EventEmitter<CallTrace>();

    @ViewChild('remoteVideo') remoteVideo?: ElementRef<HTMLVideoElement>;
    @ViewChild('localVideo') localVideo?: ElementRef<HTMLVideoElement>;

    visible = false;
    mediaType: 'audio' | 'video' = 'video';
    contactName = '';
    callState: 'idle' | 'incoming' | 'dialing' | 'connecting' | 'connected' | 'ended' | 'error' = 'idle';
    errorMessage = '';

    private destroy$ = new Subject<void>();
    private currentUserId = Number(this.authService.getAuthUserIdFromToken());
    private pendingIncomingSignal: CallSignal | null = null;
    private lastOutgoingSignature = '';
    private localStream: MediaStream | null = null;
    private remoteStream: MediaStream | null = null;
    private offerRetryTimer: number | null = null;
    private callStartedAtMs = Date.now();
    private callConnectedAtMs: number | null = null;
    private callTraceEmitted = false;
    private ringtone: HTMLAudioElement | null = null;

    constructor(
        public webrtcService: WebrtcService,
        private authService: AuthService,
        private cdr: ChangeDetectorRef,
    ) {}

    get title(): string {
        return this.mediaType === 'video' ? 'Appel video' : 'Appel vocal';
    }

    get statusLabel(): string {
        if (this.callState === 'incoming') {
            return 'Appel entrant';
        }
        if (this.callState === 'dialing') {
            return 'Appel en cours de connexion';
        }
        if (this.callState === 'connecting') {
            return 'Connexion en cours';
        }
        if (this.callState === 'connected') {
            return 'Connecte';
        }
        if (this.callState === 'ended') {
            return 'Appel termine';
        }
        if (this.callState === 'error') {
            return 'Erreur de connexion';
        }
        return this.mediaType === 'video' ? 'Popup video' : 'Popup vocal';
    }

    get hasRemoteStream(): boolean {
        return Boolean(this.remoteStream);
    }

    get hasLocalStream(): boolean {
        return Boolean(this.localStream);
    }

    get isScreenSharing(): boolean {
        return this.webrtcService.isScreenSharing();
    }

    ngOnInit(): void {
        if (!this.currentUserId || Number.isNaN(this.currentUserId)) {
            this.callState = 'error';
            this.errorMessage = "Utilisateur authentifie introuvable";
            this.visible = true;
            return;
        }

        this.webrtcService.init(this.currentUserId, this.authService.token || undefined);
        this.webrtcService.listenToSignals(this.currentUserId);
        this.bindServiceEvents();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['session'] && this.session) {
            const signature = this.getSessionSignature(this.session);
            if (signature === this.lastOutgoingSignature) {
                return;
            }

            this.lastOutgoingSignature = signature;
            this.visible = true;
            this.mediaType = this.session.mediaType;
            this.contactName = this.session.contactName;
            this.callState = 'dialing';
            this.errorMessage = '';
            this.pendingIncomingSignal = null;
            this.callStartedAtMs = Date.now();
            this.callConnectedAtMs = null;
            this.callTraceEmitted = false;
            void this.startOutgoingCall(this.session);
        }
    }

    async acceptIncomingCall(): Promise<void> {
        if (!this.pendingIncomingSignal) {
            return;
        }

        this.stopRingtone();
        this.visible = true;
        this.callState = 'connecting';
        this.errorMessage = '';

        try {
            await this.preparePeer(this.pendingIncomingSignal.mediaType || this.mediaType);
            await this.webrtcService.createAnswer(this.pendingIncomingSignal);
            this.callState = 'connected';
            this.callConnectedAtMs = Date.now();
            this.syncMediaStreams();
            this.cdr.detectChanges();
        } catch (error) {
            this.handleCallError(error);
        }
    }

    async rejectIncomingCall(): Promise<void> {
        this.emitCallTrace('rejected');
        this.stopRingtone();
        if (this.pendingIncomingSignal) {
            await this.webrtcService.endCall({
                senderId: this.currentUserId,
                receiverId: this.pendingIncomingSignal.senderId,
                conversationId: this.pendingIncomingSignal.conversationId,
                callCode: this.pendingIncomingSignal.callCode,
            });
        }

        this.resetCallState();
        this.close.emit();
    }

    async endCall(): Promise<void> {
        const traceStatus = this.callState === 'connected' ? 'ended' : this.callState === 'incoming' ? 'missed' : 'canceled';
        this.emitCallTrace(traceStatus);
        this.stopRingtone();
        const receiverId = this.session?.peerId ?? this.pendingIncomingSignal?.senderId ?? undefined;
        const conversationId = this.session?.conversationId ?? this.pendingIncomingSignal?.conversationId;

        await this.webrtcService.endCall({
            senderId: this.currentUserId,
            receiverId,
            conversationId,
            callCode: this.pendingIncomingSignal?.callCode,
        });

        this.resetCallState();
        this.close.emit();
    }

    toggleMicrophone(): void {
        const next = !this.webrtcService.isMicrophoneEnabled();
        this.webrtcService.setMicrophoneEnabled(next);
    }

    toggleCamera(): void {
        const next = !this.webrtcService.isCameraEnabled();
        this.webrtcService.setCameraEnabled(next);
        this.syncMediaStreams();
    }

    async toggleScreenShare(): Promise<void> {
        try {
            if (this.webrtcService.isScreenSharing()) {
                await this.webrtcService.stopScreenShare();
                this.syncMediaStreams();
                this.cdr.detectChanges();
                return;
            }

            const started = await this.webrtcService.startScreenShare();
            if (started) {
                this.syncMediaStreams();
                this.cdr.detectChanges();
            }
        } catch (error) {
            console.error('[QuickChat Call] screen share error', error);
            this.errorMessage = "Impossible de partager l'écran";
            this.cdr.detectChanges();
        }
    }

    async dismiss(): Promise<void> {
        if (this.callState === 'incoming') {
            await this.rejectIncomingCall();
            return;
        }

        if (this.callState === 'dialing' || this.callState === 'connecting' || this.callState === 'connected') {
            await this.endCall();
            return;
        }

        this.resetCallState();
        this.close.emit();
    }

    private bindServiceEvents(): void {
        this.webrtcService.incomingCall$.pipe(takeUntil(this.destroy$)).subscribe((signal) => {
            if (this.callState === 'dialing' || this.callState === 'connecting' || this.callState === 'connected') {
                return;
            }

            this.pendingIncomingSignal = signal;
            this.visible = true;
            this.mediaType = this.inferMediaType(signal);
            this.contactName = 'Appel entrant';
            this.callState = 'incoming';
            this.errorMessage = '';
            this.playRingtone();
            this.resetStreams(false);
            this.cdr.detectChanges();
        });

        this.webrtcService.answer$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.stopOfferRetryLoop();
            this.callState = 'connected';
            this.callConnectedAtMs = Date.now();
            this.syncMediaStreams();
            this.cdr.detectChanges();
        });

        this.webrtcService.connectionState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
            if (!this.visible) {
                return;
            }

            if (state === 'connected') {
                this.stopOfferRetryLoop();
                this.callState = 'connected';
            } else if (state === 'connecting' && this.callState !== 'incoming') {
                this.callState = 'connecting';
            } else if (state === 'disconnected' || state === 'failed') {
                this.callState = 'error';
                this.errorMessage = "La connexion WebRTC a echoue";
            } else if (state === 'closed' && this.callState !== 'ended') {
                this.callState = 'ended';
            }

            this.cdr.detectChanges();
        });

        this.webrtcService.callEnded$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.emitCallTrace(this.callState === 'incoming' ? 'missed' : this.callState === 'connected' ? 'ended' : 'canceled');
            this.stopRingtone();
            this.callState = 'ended';
            this.resetCallState(false);
            this.close.emit();
            this.cdr.detectChanges();
        });
    }

    private async startOutgoingCall(session: QuickChatCallSession): Promise<void> {
        try {
            await this.preparePeer(session.mediaType);
            await this.webrtcService.createOffer(session.conversationId, session.peerId, session.mediaType);
            this.callState = 'dialing';
            this.syncMediaStreams();
            this.startOfferRetryLoop();
            this.cdr.detectChanges();
        } catch (error) {
            this.stopOfferRetryLoop();
            this.handleCallError(error);
        }
    }

    private async preparePeer(mediaType: 'audio' | 'video'): Promise<void> {
        await this.webrtcService.initPeer(mediaType, (stream) => {
            this.remoteStream = stream;
            this.callState = 'connected';
            this.syncMediaStreams();
            this.cdr.detectChanges();
        });

        this.mediaType = mediaType;
        this.localStream = this.webrtcService.getLocalStream();
        this.syncMediaStreams();
    }

    private syncMediaStreams(): void {
        setTimeout(() => {
            if (this.remoteVideo?.nativeElement && this.remoteStream) {
                this.remoteVideo.nativeElement.muted = false;
                this.remoteVideo.nativeElement.volume = 1;
                this.remoteVideo.nativeElement.srcObject = this.remoteStream;
                void this.remoteVideo.nativeElement.play().catch(() => undefined);
            }

            if (this.localVideo?.nativeElement && this.localStream) {
                const screenStream = this.webrtcService.getScreenStream();
                const previewSource = screenStream ?? this.localStream;
                const previewVideoTracks = previewSource.getVideoTracks();
                const previewStream =
                    previewVideoTracks.length > 0 ? new MediaStream(previewVideoTracks) : new MediaStream();

                this.localVideo.nativeElement.muted = true;
                this.localVideo.nativeElement.volume = 0;
                this.localVideo.nativeElement.srcObject = previewStream;
                void this.localVideo.nativeElement.play().catch(() => undefined);
            }
        });
    }

    private resetStreams(stopTracks = true): void {
        if (stopTracks) {
            this.webrtcService.cleanUp(false);
        }
        void this.webrtcService.stopScreenShare().catch(() => undefined);

        this.localStream = null;
        this.remoteStream = null;

        if (this.remoteVideo?.nativeElement) {
            this.remoteVideo.nativeElement.srcObject = null;
        }

        if (this.localVideo?.nativeElement) {
            this.localVideo.nativeElement.srcObject = null;
        }
    }

    private resetCallState(stopTracks = true): void {
        this.stopOfferRetryLoop();
        this.stopRingtone();
        this.resetStreams(stopTracks);
        this.visible = false;
        this.mediaType = 'video';
        this.contactName = '';
        this.errorMessage = '';
        this.pendingIncomingSignal = null;
        this.lastOutgoingSignature = '';
        this.callState = 'idle';
    }

    private handleCallError(error: unknown): void {
        console.error('[QuickChat Call] erreur WebRTC', error);
        this.callState = 'error';
        this.errorMessage = 'Impossible de lancer l appel';
        this.visible = true;
        this.cdr.detectChanges();
    }

    private emitCallTrace(status: CallTrace['status']): void {
        if (this.callTraceEmitted) {
            return;
        }

        const conversationId = this.session?.conversationId ?? this.pendingIncomingSignal?.conversationId;
        const peerId = this.session?.peerId ?? this.pendingIncomingSignal?.senderId;

        if (!conversationId || !peerId) {
            return;
        }

        const endAt = Date.now();
        const startedAt = this.callConnectedAtMs ?? this.callStartedAtMs;
        const durationSeconds = Math.max(0, Math.round((endAt - startedAt) / 1000));

        this.callTraceEmitted = true;
        this.callFinished.emit({
            conversationId,
            peerId,
            mediaType: this.session?.mediaType ?? this.mediaType,
            status,
            durationSeconds,
            endedAtEpochMs: endAt,
            contactName: this.contactName || this.session?.contactName,
        });
    }

    private getSessionSignature(session: QuickChatCallSession): string {
        return `${session.conversationId}:${session.peerId}:${session.mediaType}`;
    }

    private startOfferRetryLoop(): void {
        if (this.offerRetryTimer) {
            return;
        }

        this.offerRetryTimer = window.setInterval(() => {
            if (!this.session || this.callState !== 'dialing') {
                this.stopOfferRetryLoop();
                return;
            }

            this.webrtcService
                .createOffer(this.session.conversationId, this.session.peerId, this.session.mediaType)
                .catch(() => undefined);
        }, 3000);
    }

    private stopOfferRetryLoop(): void {
        if (!this.offerRetryTimer) {
            return;
        }

        window.clearInterval(this.offerRetryTimer);
        this.offerRetryTimer = null;
    }

    private inferMediaType(signal: CallSignal): 'audio' | 'video' {
        if (signal.mediaType) {
            return signal.mediaType;
        }

        if (signal.sdp?.includes('m=video')) {
            return 'video';
        }

        return 'audio';
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.stopOfferRetryLoop();
        this.stopRingtone();
        this.resetStreams();
    }

    private playRingtone(): void {
        this.stopRingtone();
        this.ringtone = new Audio('/sounds/classic.mp3');
        this.ringtone.loop = true;
        void this.ringtone.play().catch(() => undefined);
    }

    private stopRingtone(): void {
        if (!this.ringtone) {
            return;
        }

        this.ringtone.pause();
        this.ringtone.currentTime = 0;
        this.ringtone = null;
    }
}
